import {queryForRepos} from './task-chat-input-toolbar/TaskChatInputToolbar'
import type {CopilotChatRepo, CopilotCustomInstructions, ImageReference} from '@github-ui/copilot-chat-types'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {useLastUsedRepository} from '@github-ui/copilot-shared-ui/repos/use-last-used-repository'
import {useEntitlement} from '@github-ui/copilot-entitlement/EntitlementContext'
import {
  useEntitlementQuotaNotice,
  type EntitlementQuotaNotice,
} from '@github-ui/copilot-shared-ui/hooks/use-entitlement-quota-notice'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {announce} from '@github-ui/aria-live'
import {ChatInputValidationError} from '@github-ui/copilot-chat-state/validation-error'
import {submitWithInteractionEvent} from './submit-with-interaction-event'
import {
  clientInteractionAccepted,
  clientInteractionForEvent,
  clientInteractionPending,
  prepareClientInteraction,
} from '@github-ui/copilot-browser-latency/copilot-client-interaction'
import {recordClientCommandPreparation} from '@github-ui/ahp-session-client/client-interaction-observer'
import {
  use,
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEventHandler,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react'
import {ComposerAttachmentScopeContext} from '@github-ui/copilot-shared-ui/contexts/ComposerAttachmentScopeContext'
import {
  useInjectedReferencesActions,
  useInjectedReferencesData,
  type InjectedAttachment,
} from '@github-ui/copilot-shared-ui/contexts/InjectedReferencesContext'
import {isAttachmentInScope} from '@github-ui/copilot-shared-ui/attachments/attachment-scope'
import {useSearchParams} from '@github-ui/use-navigate'
import {
  captureDirectTurnTiming,
  createPreparedPrompt,
  type DirectTurnTelemetrySeed,
  type AhpProvisioningTarget,
  type PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import type {MessageAttachment} from '@microsoft/agent-host-protocol'
import {isSubmissionAbandoned} from '@github-ui/ahp-session-client/submission-error'
import type {
  ChatComposerValueChangeReason,
  ComposerAttachment,
} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import {
  collectExplicitAttachmentKeys,
  useComposerAttachments,
  type ComposerAttachmentsController,
} from '@github-ui/copilot-shared-ui/hooks/use-composer-attachments'
import {usePasteReferences} from '@github-ui/copilot-shared-ui/attachments/use-paste-references'
import {implicitAttachmentKey} from '@github-ui/copilot-shared-ui/references/implicit-context'
import type {AhpSpaceSelection} from '@github-ui/copilot-shared-ui/contexts/EstablishedAhpComposerContext'
import type {AutoTierPickerConfig} from '@github-ui/copilot-shared-ui/components/chat-model-picker/ModelPickerDesign'

import type {DropdownRepository} from './repository'
import {useAgentModels} from '@github-ui/copilot-shared-ui/agents/use-agent-models'
import {agentModelScopeKey} from './use-latest-agent-model'
import {useManagedDefaultModel, useModelSelectionLoadingAnnouncement} from './use-managed-default-model'
import {agentSupportsModelSelection, isCopilotCodingAgent} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {useLastSelectedAgent} from './use-last-selected-agent'
import {useAgentVendorModel} from './use-agent-vendor-model'
import {useLatestReasoningEffort} from './use-latest-reasoning-effort'
import {
  useModelSupportedReasoningEfforts,
  useReasoningEffortAutoFallback,
  useSelectModelClearingReasoningEffort,
} from './use-model-supported-reasoning-efforts'
import type {DropTargetProps} from './use-file-drop-target'
import {useFileDropTarget} from './use-file-drop-target'
import type {ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {SANDBOX_ENVIRONMENT_OPTION, type EnvironmentOption} from './environment-strip/environment-option'
import type {SelectedAgent} from '@github-ui/agent-sessions-types/agent-identity'
import {useComposerImageReferences} from './use-composer-image-references'
import {implicitAttachmentChips, useImplicitDraftAttachments} from './use-implicit-draft-attachments'
import {
  isCustomAgentPickerAvailable,
  isSandboxComposedDraft,
  resolvePersistedEnvironmentOption,
  useSelectedRepositorySandboxPolicy,
  useEnvironmentSelection,
} from './use-environment-selection'
import {MAX_AGENT_TASK_PROMPT_LENGTH} from '@github-ui/copilot-shared-ui/composer/max-prompt-length'
import {
  dispatchEnvironmentSubmissionWithResult,
  type EnvironmentSubmissionDispatchResult,
} from './environment-strip/dispatch-environment-submission'
import {useTaskComposerScope, type TaskComposerScopeState} from './use-task-composer-scope'
import {
  type ChronicleCommand,
  useChronicleAutocomplete,
} from '@github-ui/copilot-shared-ui/hooks/use-chronicle-autocomplete'
import {DEFAULT_MCP_PERMISSION_MODE, type McpPermissionMode} from '@github-ui/agent-sessions-types/mcp-permission-mode'
import {useLatestMcpPermissionMode} from './use-latest-mcp-permission-mode'
import type {TaskComposerTargetSelection} from '@github-ui/copilot-shared-ui/utils/task-composer-target-selection'
import {
  buildPromptWithReferenceContext,
  canPrepareTaskComposerChat,
  type ChatSubmissionMetadata,
  type AvailableOrganization,
  type CreateTaskReference,
  getChatDraftStorageKeySuffix,
  getDraftStorageKeySuffix,
  isRepoInScope,
  isAttachedRepoInScope,
  makeFileOrSnippetReference,
  scopeLogin,
  activeComposerScope,
  type TaskComposerProps,
  type TaskComposerScope,
  resolveAvailableAgentMode,
  resolveMcpPermissionGate,
  resolveMcpPermissionMode,
  resolvePickerScopeOwner,
  resolveTaskComposerQuotaKind,
} from './shared'
import {fetchRepo} from '@github-ui/copilot-shared-ui/repos/fetch-copilot-chat-repo'
import {useNewChatPreset} from './use-new-chat-preset'
import {useComposerTelemetry} from '../telemetry/ComposerTelemetryContext'
import {
  repositoryInstructionIds,
  useSelectedRepositoryInstructions,
} from '../shared/use-selected-repository-instructions'
import {DEFAULT_AUTO_MODEL_TIER, mergeModelSelectionConfig, sanitizeAutoTierConfig} from '../shared/auto-tier'

export interface TaskComposerImageState {
  onPaste: ClipboardEventHandler<HTMLTextAreaElement>
  dropTargetProps: DropTargetProps
  isDraggedOver: boolean
  isUploading: boolean
  /** When omitted the controller derives the limit from image URLs in the current prompt. */
  isAtImageLimit?: boolean
}

export interface TaskComposerTargetState {
  targetSelection: TaskComposerTargetSelection
  setTargetSelection: Dispatch<SetStateAction<TaskComposerTargetSelection>>
  selectedRepo: DropdownRepository | null
  textAreaRef: RefObject<HTMLTextAreaElement | null>
}

export function useTaskComposerTargetState(initialSelection?: TaskComposerTargetSelection): TaskComposerTargetState {
  const [targetSelection, setTargetSelection] = useState<TaskComposerTargetSelection>(
    initialSelection ?? {kind: 'unselected'},
  )
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const selectedRepo = targetSelection.kind === 'repository' ? targetSelection.repository : null
  return {targetSelection, setTargetSelection, selectedRepo, textAreaRef}
}

interface TaskComposerDraftState {
  currentPrompt: string
  setCurrentPrompt: Dispatch<SetStateAction<string>>
  draftThreadId: string
  /** Clears the draft through its owner, so this hook never writes the shared key. */
  clearDraft: (submittedContent: string) => void
}

interface TaskComposerBranchState {
  selectedBranch: string
  setSelectedBranch: Dispatch<SetStateAction<string>>
}

interface TaskComposerEnvironmentState {
  selectedEnvironment: EnvironmentOption | null
  setSelectedEnvironment: Dispatch<SetStateAction<EnvironmentOption | null>>
  isAhpEnvironment: boolean
  isSandboxEnvironment: boolean
  showSandboxOption: boolean
  showEnvironmentPicker: boolean
  isSandboxPolicyPending?: boolean
}

export interface TaskComposerInputs {
  targetState: TaskComposerTargetState
  images: TaskComposerImageState
  /** Lets a new-session parent keep one draft while the destination switches. */
  draft?: TaskComposerDraftState
  /** Lets a new-session parent keep task branch selection across destination switches. */
  branch?: TaskComposerBranchState
  /** Lets a new-session parent own environment selection alongside the destination. */
  environment?: TaskComposerEnvironmentState
  /** Lets a new-session parent keep one Sandbox scope across destination switches. */
  scope?: TaskComposerScopeState
}

/** Keeps the unused local scope hook from re-deriving on every organizations change. */
const EMPTY_ORGANIZATIONS: AvailableOrganization[] = []
/** Stable "Copilot default" agent, so an environment without a picker submits no agent. */
const EMPTY_SELECTED_AGENT: SelectedAgent = {}

async function dispatchEnvironmentSubmissionOrThrow(
  input: Parameters<typeof dispatchEnvironmentSubmissionWithResult>[0],
  onError: () => void,
): Promise<EnvironmentSubmissionDispatchResult> {
  try {
    return await dispatchEnvironmentSubmissionWithResult(input)
  } catch (error) {
    onError()
    const message = error instanceof Error ? error.message : 'Failed to start task in this environment.'
    throw new ChatInputValidationError(message)
  }
}

async function fetchRepoSafely(id: number) {
  try {
    return await fetchRepo(id)
  } catch {
    return undefined
  }
}

async function queryForInitialRepo(nameWithOwner: string) {
  try {
    const repositories = await queryForRepos(nameWithOwner)
    return repositories.find(repository => repository.nameWithOwner === nameWithOwner)
  } catch {
    return undefined
  }
}

async function selectInitialRepo(
  nameWithOwner: string,
  isCancelled: () => boolean,
  onSelect: (repository: DropdownRepository) => void,
) {
  const repository = await queryForInitialRepo(nameWithOwner)
  if (!isCancelled() && repository?.enabled) {
    onSelect(repository)
  }
}

export interface TaskComposerController {
  // Prompt + draft
  currentPrompt: string
  setCurrentPrompt: Dispatch<SetStateAction<string>>
  handleTextChange: (value: string | ((current: string) => string), reason?: ChatComposerValueChangeReason) => void
  handleChronicleSelect: (command: string) => void
  setChronicleDismissed: Dispatch<SetStateAction<boolean>>
  chronicleSuggestions: ChronicleCommand[]
  draftThreadId: string

  // Repo / branch / agent selection
  targetSelection: TaskComposerTargetSelection
  selectedRepo: DropdownRepository | null
  provisioningRepository: string | undefined
  selectedBranch: string
  handleTargetSelection: (selection: TaskComposerTargetSelection) => void
  setSelectedBranch: Dispatch<SetStateAction<string>>
  handleRepoSelect: (repo: DropdownRepository | undefined) => void
  selectedAgent: SelectedAgent
  setSelectedAgent: (agent: SelectedAgent) => void
  initialNameWithOwner: string
  hasSpecifiedRepository: boolean
  branchParam: string | null

  // Scope selector state — only exposed while the Sandbox environment is active,
  // since the picker (and any downstream owner) is Sandbox-only.
  selectedScope: TaskComposerScope | undefined
  setSelectedScope: Dispatch<SetStateAction<TaskComposerScope>>
  availableOrganizations: AvailableOrganization[]
  currentUserAvatarUrl: string | undefined
  /**
   * Owner login the repo picker should scope its queries to (personal → undefined).
   * Only defined while the Sandbox environment is active, since the ScopePicker
   * (and any downstream owner filter) is Sandbox-only.
   */
  scopeOwner: string | null | undefined
  attachmentScopeOwner: string | null | undefined
  /**
   * Owner login the attach pickers filter their results by. Unlike
   * `attachmentScopeOwner`, which also governs which attachments a draft keeps,
   * this narrows only what the pickers offer, so a repo-scoped draft lists just
   * its repository owner's repositories without discarding anything already
   * attached from elsewhere.
   */
  pickerScopeOwner: string | null | undefined

  // References
  reference: CreateTaskReference | null
  setReference: Dispatch<SetStateAction<CreateTaskReference | null>>
  chatReference: ReturnType<typeof makeFileOrSnippetReference> | undefined
  imageReferences: ImageReference[]

  // Model + reasoning effort
  shouldShowModelPicker: boolean
  selectedModelId: string | undefined
  selectModel: (modelId: string) => void
  isModelSelectionReady: boolean
  vendorFilters: string[] | undefined
  isTokenBasedBillingEnabled: boolean
  autoTierPicker: AutoTierPickerConfig | undefined
  selectedReasoningEffort: ReasoningEffort | undefined
  setSelectedReasoningEffort: (value: ReasoningEffort | undefined) => void
  supportedReasoningEfforts: string[] | undefined

  // Environments + MCP
  selectedEnvironment: EnvironmentOption | null
  setSelectedEnvironment: (environment: EnvironmentOption) => void
  isAhpEnvironment: boolean
  isSandboxEnvironment: boolean
  isSandboxDraft: boolean
  isSandboxPolicyBlockingSubmission: boolean
  isRepositorySelectionValid: boolean
  isEnvironmentSubmitting: boolean
  canPrepareChat: boolean
  isScopeBlockedBySso: boolean
  isSelectedEnvironmentSubmissionAvailable: boolean
  showSandboxOption: boolean
  showEnvironmentPicker: boolean
  mcpPermissionMode: McpPermissionMode
  setMcpPermissionMode: (value: McpPermissionMode) => void
  selectedSpace: AhpSpaceSelection | undefined
  setSelectedSpace: Dispatch<SetStateAction<AhpSpaceSelection | undefined>>
  isSpaceResolving: boolean
  setIsSpaceResolving: Dispatch<SetStateAction<boolean>>
  spaceSelectionError: string | undefined
  setSpaceSelectionError: Dispatch<SetStateAction<string | undefined>>

  // Feature flags
  isSubAgentsEnabled: boolean
  isReasoningEffortEnabled: boolean
  isMcpPermissionGateEnabled: boolean

  // Derived misc
  isCopilotAgent: boolean
  isBlockedByQuota: boolean
  quotaNotice: EntitlementQuotaNotice | null
  isAtImageLimit: boolean
  resolvedUserLogin: string | undefined

  // Image passthrough
  onPaste: TaskComposerImageState['onPaste'] | undefined
  dropTargetProps: DropTargetProps
  isDraggedOver: boolean
  isImageUploading: boolean

  /**
   * Draft attachments sent inline with the AHP prompt. Only wired up while an AHP
   * environment is selected; the cloud path uploads images to dotcom and inlines
   * them as markdown instead.
   */
  environmentAttachments: ComposerAttachmentsController
  /** Attachments kept for the repo-less Sandbox Chat draft. */
  chatAttachments: ComposerAttachmentsController
  /**
   * Chips for the host page the draft was composed on, resolved from the same
   * page context the established composer reads. Empty for a task draft, and
   * for any host that supplies no page.
   */
  implicitChips: ComposerAttachment[]
  implicitAttachments: MessageAttachment[]
  dismissImplicitChip: (chipId: string) => void
  injectedReferences: InjectedAttachment[]
  removeInjectedReference: (id: string) => void

  // Refs + submit
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  attachReferenceUrl: (url: string, privateRepository?: boolean) => void
  onSubmit: (
    content: string,
    directTurnTelemetrySeed?: DirectTurnTelemetrySeed,
    suppressDirectTurnTelemetryCapture?: boolean,
  ) => boolean | void | Promise<boolean | void>
  onSubmitInteraction?: (
    event: Event,
    ...args: Parameters<TaskComposerController['onSubmit']>
  ) => ReturnType<TaskComposerController['onSubmit']>

  // Passthrough props the shared toolbar needs
  hideRepoSelector: boolean | undefined
  eventType: string | undefined
  initialBranch: string | undefined
}

export function useTaskComposer(
  props: TaskComposerProps,
  {targetState, images, draft, branch, environment, scope}: TaskComposerInputs,
): TaskComposerController {
  const {
    initialRepo,
    initialRepositoryNameWithOwner,
    initialReference,
    hideRepoSelector,
    eventType,
    initialText,
    enableQuickChatDraftRecovery = false,
    initialPreparedPrompt,
    initialTaskAttachments,
    initialChatAttachments,
    initialChatProvisioningTarget,
    initialSpace,
    initialBranch,
    onEnvironmentSubmit,
    chatLaunch,
    newChatPresetId,
    supportsManagedSandbox = false,
    agentQuotaSnapshot,
    handleUserSubmit,
    agentMode,
    approvalMode,
    mcpPermissionGateEnabled,
    currentUserLogin,
    currentUserAvatarUrl,
    availableOrganizations = [],
    lastContributedOrganizationLogin,
    initialTarget,
    suppressUrlRepository = false,
    includeImplicitContextForTask = false,
    directTurnTelemetrySurface,
    preserveDirectTurnTelemetryForHandoff = false,
  } = props

  const entitlement = useEntitlement()
  const {tokenBasedBillingEnabled: isTokenBasedBillingEnabled} = entitlement
  const {
    models: availableModels,
    defaultModelId: managedDefaultModelId,
    isLoading: isLoadingModels,
    error: modelsError,
  } = useAgentModels()

  const [lastUsedRepo, setLastUsedRepo] = useLastUsedRepository()
  const {targetSelection, setTargetSelection, selectedRepo, textAreaRef} = targetState
  const [hadInitialChatRepositoryAttachment] = useState(() =>
    initialChatProvisioningTarget?.kind === 'repository'
      ? initialChatAttachments?.some(
          attachment =>
            attachment.displayKind === 'repository' && attachment.label === initialChatProvisioningTarget.nameWithOwner,
        )
      : undefined,
  )

  const [searchParams] = useSearchParams()
  const isValidRepo = initialRepo && Object.keys(initialRepo).length > 0
  const nwoParam = initialTarget || suppressUrlRepository ? null : searchParams.get('repo')
  const branchParam = initialTarget || suppressUrlRepository ? null : searchParams.get('branch')
  const hasSpecifiedRepository = Boolean(isValidRepo || initialRepositoryNameWithOwner || nwoParam)
  let initialNameWithOwner = ''
  if (isValidRepo) {
    initialNameWithOwner = `${initialRepo.ownerLogin}/${initialRepo.name}`
  } else if (initialRepositoryNameWithOwner) {
    initialNameWithOwner = initialRepositoryNameWithOwner
  } else if (nwoParam) {
    initialNameWithOwner = nwoParam
  } else if (lastUsedRepo) {
    initialNameWithOwner = `${lastUsedRepo.ownerLogin}/${lastUsedRepo.name}`
  }

  const draftScope =
    targetSelection.kind === 'chat'
      ? 'chat'
      : eventType === 'agents_panel'
        ? undefined
        : (selectedRepo?.nameWithOwner ?? initialNameWithOwner)
  const internalDraftThreadId =
    targetSelection.kind === 'chat'
      ? getChatDraftStorageKeySuffix(eventType)
      : getDraftStorageKeySuffix(eventType, draftScope)
  const [initialRecoveryDraft] = useState(() => copilotLocalStorage.getSavedUserMessageOnError(internalDraftThreadId))
  // Recovery is a Quick Chat concept, so it must not consume a repository's error draft.
  const recoversQuickChatDraft = enableQuickChatDraftRecovery && targetSelection.kind === 'chat'
  const recoveryTarget = recoversQuickChatDraft ? internalDraftThreadId : undefined
  const clearedRecoveryDraftRef = useRef<string | undefined>(undefined)

  const [uncontrolledCurrentPrompt, setUncontrolledCurrentPrompt] = useState(
    () =>
      initialText ??
      (searchParams.get('prompt') ||
        initialRecoveryDraft ||
        copilotLocalStorage.getSavedMessage(internalDraftThreadId, 'task') ||
        ''),
  )
  const currentPrompt = draft?.currentPrompt ?? uncontrolledCurrentPrompt
  const setCurrentPrompt = draft?.setCurrentPrompt ?? setUncontrolledCurrentPrompt
  const draftThreadId = draft?.draftThreadId ?? internalDraftThreadId

  // Chat can become the active target after mount, so adopt the one-shot failed prompt
  // then. Narrower than the per-destination reload main removed: Quick Chat only, and
  // only ever reads the error entry.
  const [armedRecoveryTarget, setArmedRecoveryTarget] = useState<string | undefined>(recoveryTarget)
  if (recoveryTarget !== armedRecoveryTarget) {
    setArmedRecoveryTarget(recoveryTarget)
    if (recoveryTarget) {
      const recoveredPrompt = copilotLocalStorage.getSavedUserMessageOnError(recoveryTarget)
      if (recoveredPrompt) setCurrentPrompt(recoveredPrompt)
    }
  }

  // Storage is scoped to the active destination for restoration on a future mount.
  // The mounted composer keeps one in-memory draft while the destination changes.
  // A controlled draft is persisted by its owner instead, so the two never race.
  useEffect(() => {
    if (draft) return
    // Leaving the chat target clears the ref, re-arming consumption for the next visit.
    if (!recoversQuickChatDraft) {
      clearedRecoveryDraftRef.current = undefined
      copilotLocalStorage.setSavedMessage(draftThreadId, 'task', currentPrompt)
      return
    }
    if (
      clearedRecoveryDraftRef.current !== draftThreadId &&
      copilotLocalStorage.getSavedUserMessageOnError(draftThreadId)
    ) {
      copilotLocalStorage.setSavedMessageFast(draftThreadId, 'task', currentPrompt)
      copilotLocalStorage.clearSavedUserMessageOnError(draftThreadId)
      clearedRecoveryDraftRef.current = draftThreadId
      return
    }
    copilotLocalStorage.setSavedMessage(draftThreadId, 'task', currentPrompt)
  }, [draft, draftThreadId, currentPrompt, recoversQuickChatDraft])

  const allChronSuggestions = useChronicleAutocomplete(currentPrompt)
  const [chronicleDismissed, setChronicleDismissed] = useState(false)
  const {injected: allInjectedReferences, skipImplicitReferences} = useInjectedReferencesData()
  const {removeInjected: removeInjectedReference, clearInjected} = useInjectedReferencesActions()
  // A first turn carries the page it was composed on, exactly as every later
  // turn does. Repository-backed tasks opt in only when their host surface
  // intentionally treats the page as implicit context.
  const {
    attachments: resolvedImplicitAttachments,
    dismissChip: dismissImplicitChip,
    resolve: resolveImplicitAttachments,
    settle: settleImplicitAttachments,
  } = useImplicitDraftAttachments({
    enabled: (targetSelection.kind === 'chat' || includeImplicitContextForTask) && !skipImplicitReferences,
  })
  const handleChronicleSelect = useCallback(
    (command: string) => {
      setCurrentPrompt(command)
      setChronicleDismissed(false)
    },
    [setChronicleDismissed, setCurrentPrompt],
  )

  const [referenceRepo, setReferenceRepo] = useState<CopilotChatRepo | null>(null)
  const [uncontrolledSelectedBranch, setUncontrolledSelectedBranch] = useState<string>(initialBranch || '')
  const selectedBranch = branch?.selectedBranch ?? uncontrolledSelectedBranch
  const setSelectedBranch = branch?.setSelectedBranch ?? setUncontrolledSelectedBranch
  const [previousRepoParam, setPreviousRepoParam] = useState<string | null>(null)
  const agentParam = searchParams.get('agent')
  const preselectedAgentFromParam: SelectedAgent | undefined = agentParam
    ? {typeName: 'customSubAgent', subAgentSlug: agentParam}
    : undefined
  const [selectedAgent, setSelectedAgent] = useLastSelectedAgent(preselectedAgentFromParam)
  const {supportsSandbox: supportsSelectedRepositorySandbox, isPending: isRepositorySandboxPolicyPending} =
    useSelectedRepositorySandboxPolicy({
      selectedRepositoryId: selectedRepo?.id,
      initialRepositoryId: initialRepo?.id,
      supportsManagedSandbox,
    })
  // After returning from an SSO redirect kicked off by the ScopePicker, restore
  // the scope to the org the user had picked and the Sandbox environment they
  // were on. A caller that owns the destination supplies this instead, so the
  // scope survives a switch between the task and Chat surfaces. Persistence is
  // Sandbox-only: an actions-env-only surface (`supportsManagedSandbox=false`)
  // must not read or write the persisted scope.
  const nwoParamOwner = nwoParam?.split('/', 1)[0] ?? undefined
  const localScope = useTaskComposerScope(
    scope ? EMPTY_ORGANIZATIONS : availableOrganizations,
    supportsSelectedRepositorySandbox,
    nwoParamOwner,
    currentUserLogin,
    lastContributedOrganizationLogin,
    Boolean(scope) && copilotFeatureFlags.oneCopilotChatOwnerSplitPicker,
  )
  const {selectedScope, setSelectedScope, pendingSsoOrgLogin} = scope ?? localScope

  const isSubAgentsEnabled = isFeatureEnabled('copilot_swe_agent_use_subagents')
  const isReasoningEffortEnabled = copilotFeatureFlags.codingAgentReasoningEffort
  const isMcpPermissionGateEnabled = resolveMcpPermissionGate(mcpPermissionGateEnabled)
  const [selectedAutoTier, setSelectedAutoTier] = useState(DEFAULT_AUTO_MODEL_TIER)

  const [reference, setReference] = useState<CreateTaskReference | null>(initialReference || null)
  const [mcpPermissionMode, setMcpPermissionMode] = useLatestMcpPermissionMode()
  const [isEnvironmentSubmitting, setIsEnvironmentSubmitting] = useState(false)
  const [selectedSpace, setSelectedSpace] = useState<AhpSpaceSelection | undefined>(initialSpace)
  const [isSpaceResolving, setIsSpaceResolving] = useState(false)
  const [spaceSelectionError, setSpaceSelectionError] = useState<string>()

  const localEnvironment = useEnvironmentSelection({
    onEnvironmentSubmit: environment ? undefined : onEnvironmentSubmit,
    supportsManagedSandbox: environment ? false : supportsSelectedRepositorySandbox,
    sandboxPolicyPending: !environment && isRepositorySandboxPolicyPending,
    // Chat and the ScopePicker SSO handshake require Sandbox; other drafts
    // restore the user's most recent environment so `?repo=` remounts stop
    // snapping back to Sandbox.
    initialEnvironment: environment
      ? null
      : initialTarget === 'chat' || pendingSsoOrgLogin
        ? SANDBOX_ENVIRONMENT_OPTION
        : (resolvePersistedEnvironmentOption({
            supportsManagedSandbox: supportsSelectedRepositorySandbox || isRepositorySandboxPolicyPending,
            hasEnvironmentSubmit: Boolean(onEnvironmentSubmit),
          }) ?? null),
  })
  const {
    selectedEnvironment,
    setSelectedEnvironment: setSelectedEnvironmentState,
    isAhpEnvironment,
    isSandboxEnvironment,
    showSandboxOption,
    showEnvironmentPicker,
    isSandboxPolicyPending = false,
  } = environment ?? localEnvironment
  const fixedRepositoryOwner =
    hideRepoSelector && hasSpecifiedRepository ? initialNameWithOwner.split('/')[0] : undefined
  // Repo-scoped composers have no scope picker, so their fixed repository owner
  // remains authoritative when Sandbox is selected.
  const launchScopeLogin = scope?.launchScopeLogin ?? scopeLogin(selectedScope, currentUserLogin)
  const conversationScopeOwner = use(ComposerAttachmentScopeContext)
  const scopeOwner = isSandboxEnvironment
    ? fixedRepositoryOwner || (conversationScopeOwner !== undefined ? conversationScopeOwner : launchScopeLogin || null)
    : undefined
  const isAgentPickerAvailable = isCustomAgentPickerAvailable({
    isSubAgentsEnabled,
    hasSelectedRepository: Boolean(selectedRepo),
  })
  // AHP accepts repository custom agents, but not top-level task agents. Keep a
  // cached top-level choice intact for a switch back to Actions while preventing
  // it from riding along with a Sandbox or local-environment submission.
  // One gated scope for every consumer: the attach pickers and the attachment
  // controller both read this, so what a picker offers and what an attachment
  // keeps cannot drift apart.
  const exposedSelectedScope = activeComposerScope(selectedScope, isSandboxEnvironment)
  // The picker is withheld on Sandbox, so the agent it last chose must not ride
  // along in the submission. Deriving it here rather than clearing the stored
  // selection keeps the choice intact for a switch back to a coding-agent
  // environment, where the picker is offered again.
  const effectiveSelectedAgent: SelectedAgent = useMemo(
    () =>
      isAgentPickerAvailable && (!isAhpEnvironment || selectedAgent.typeName === 'customSubAgent')
        ? selectedAgent
        : EMPTY_SELECTED_AGENT,
    [isAgentPickerAvailable, isAhpEnvironment, selectedAgent],
  )
  const hasImplicitChatAvailability = chatLaunch?.kind === 'environment' && chatLaunch.availability === 'implicit'
  const isSandboxDraft = isSandboxComposedDraft({
    isSandboxEnvironment,
    selectedEnvironmentKind: selectedEnvironment?.kind,
    deliversByHandoff: chatLaunch?.kind === 'handoff' || hasImplicitChatAvailability,
  })
  const modelScopeAgent =
    (isAhpEnvironment || isSandboxDraft) && selectedAgent.typeName !== 'customSubAgent'
      ? EMPTY_SELECTED_AGENT
      : selectedAgent
  const {
    agentSlug,
    vendorFilters,
    selectedModelId: persistedModelId,
    setSelectedModelId,
    isVendorScopeLoading,
  } = useAgentVendorModel(modelScopeAgent)
  const [selectedReasoningEffort, setSelectedReasoningEffort] = useLatestReasoningEffort()
  const selectModelBase = useSelectModelClearingReasoningEffort(
    setSelectedModelId,
    selectedReasoningEffort,
    setSelectedReasoningEffort,
  )
  const {
    selectedModelId,
    selectModel,
    isSelectionReady: isModelSelectionReady,
    isUsingManagedDefault,
  } = useManagedDefaultModel(
    managedDefaultModelId,
    isLoadingModels || isVendorScopeLoading,
    agentModelScopeKey(vendorFilters),
    persistedModelId,
    selectModelBase,
  )
  const supportedReasoningEfforts = useModelSupportedReasoningEfforts(selectedModelId)
  const selectedReasoningEffortForSession = isUsingManagedDefault ? undefined : selectedReasoningEffort
  useReasoningEffortAutoFallback(
    selectedReasoningEffortForSession,
    supportedReasoningEfforts,
    setSelectedReasoningEffort,
  )
  // No topLevelAgentId means the default Copilot agent is selected (empty SelectedAgent object)
  const isCopilotAgent = !modelScopeAgent.topLevelAgentId || isCopilotCodingAgent(agentSlug)
  const chronicleSuggestions = isCopilotAgent && !chronicleDismissed ? allChronSuggestions : []
  const hasSpecifiedTaskRepository = hasSpecifiedRepository && targetSelection.kind !== 'chat'
  const canPrepareChat = canPrepareTaskComposerChat({
    chatLaunch: hasImplicitChatAvailability ? {kind: 'handoff'} : chatLaunch,
    hasEnvironmentSubmit: Boolean(onEnvironmentSubmit),
    hideRepoSelector: Boolean(hideRepoSelector),
    showSandboxOption,
    hasSpecifiedRepository: hasSpecifiedTaskRepository,
    scopeLogin: launchScopeLogin,
  })
  const isAhpEnvironmentDraft = isAhpEnvironment || isSandboxDraft
  const autoTierPicker: AutoTierPickerConfig | undefined =
    copilotFeatureFlags.autoModeV2 && isSandboxDraft
      ? {
          selectedTier: selectedAutoTier,
          disabled: isEnvironmentSubmitting,
          onTierSelected: setSelectedAutoTier,
        }
      : undefined
  const attachmentScopeOwner = scopeOwner
  // A repo-scoped composer has no repo selector, so its fixed owner is the only
  // fallback available; every other draft falls back to whichever repository it
  // currently targets, so the pickers offer that repository's owner.
  const draftRepositoryOwner = selectedRepo?.owner ?? fixedRepositoryOwner
  const pickerScopeOwner = resolvePickerScopeOwner(attachmentScopeOwner, draftRepositoryOwner)
  const injectedReferences = useMemo(
    () =>
      isAhpEnvironmentDraft
        ? allInjectedReferences.filter(item => isAttachmentInScope(item.ahp, attachmentScopeOwner))
        : [],
    [allInjectedReferences, attachmentScopeOwner, isAhpEnvironmentDraft],
  )
  // Pasting a link to the page the draft already carries implicitly would
  // attach it twice, so the paste hook is told what is already there.
  const draftReferences = usePasteReferences({
    activeImplicitAttachments: resolvedImplicitAttachments,
    scopeOwner: attachmentScopeOwner,
  })
  const handleTextChange = useCallback(
    (value: string | ((current: string) => string), reason: ChatComposerValueChangeReason = 'input') => {
      // Preserve functional updates so a failed submit cannot overwrite newer text.
      setCurrentPrompt(value)
      setChronicleDismissed(false)
      if (reason !== 'submit-clear' && typeof value === 'string') draftReferences.reconcileFromText(value)
    },
    [draftReferences, setChronicleDismissed, setCurrentPrompt],
  )
  const attachmentScopeRevision = useMemo(() => ({owner: attachmentScopeOwner}), [attachmentScopeOwner])
  const activeAttachmentScopeRef = useRef(attachmentScopeRevision)
  useLayoutEffect(() => {
    activeAttachmentScopeRef.current = attachmentScopeRevision
  }, [attachmentScopeRevision])
  const handleTargetSelection = useCallback(
    (selection: TaskComposerTargetSelection) => {
      if (activeAttachmentScopeRef.current !== attachmentScopeRevision) return
      if (selection.kind === 'repository' && !isRepoInScope(selection.repository, scopeOwner)) return
      setTargetSelection(selection)
      if (selection.kind !== 'repository') setSelectedBranch('')
    },
    [attachmentScopeRevision, scopeOwner, setTargetSelection, setSelectedBranch],
  )
  const applyAutoSelectedRepo = useEffectEvent((repo: DropdownRepository) => {
    if (suppressUrlRepository || targetSelection.kind !== 'unselected' || !isRepoInScope(repo, scopeOwner)) return
    handleTargetSelection({kind: 'repository', repository: repo})
    setSelectedBranch(initialBranch ?? branchParam ?? repo.defaultBranch)
  })
  const taskAttachments = useComposerAttachments({
    references: draftReferences,
    initialAttachments: initialTaskAttachments,
    scopeOwner: attachmentScopeOwner,
    announceAnchorRef: textAreaRef,
  })
  const chatAttachments = useComposerAttachments({
    references: draftReferences,
    initialAttachments: initialChatAttachments,
    scopeOwner: attachmentScopeOwner,
    // Without an anchor the rejection hook stays silent, so a file the surface
    // refuses is only reported visually.
    announceAnchorRef: textAreaRef,
  })
  const initialChatRepositoryRemoved =
    hadInitialChatRepositoryAttachment === true &&
    initialChatProvisioningTarget?.kind === 'repository' &&
    !chatAttachments.tracked.some(
      attachment =>
        attachment.ahp.displayKind === 'repository' &&
        attachment.ahp.label === initialChatProvisioningTarget.nameWithOwner,
    )
  const activeInitialChatRepository =
    initialChatProvisioningTarget?.kind === 'repository' && !initialChatRepositoryRemoved
      ? initialChatProvisioningTarget.nameWithOwner
      : undefined
  const isScopeBlockedBySso =
    chatLaunch?.kind === 'environment' && Boolean(scope?.blockedScopeLogin) && !activeInitialChatRepository
  const environmentAttachments = targetSelection.kind === 'chat' ? chatAttachments : taskAttachments
  // Attaching something explicitly replaces the implicit chip for the same
  // entity rather than sitting beside a duplicate of it. Pasted references are
  // excluded here because they already dedupe against the implicit list, and
  // reading them back would make the two definitions circular.
  const explicitAttachmentKeys = useMemo(
    () =>
      collectExplicitAttachmentKeys(
        environmentAttachments.selectedRepos,
        environmentAttachments.tracked,
        injectedReferences,
        [],
      ),
    [environmentAttachments.selectedRepos, environmentAttachments.tracked, injectedReferences],
  )
  const implicitAttachments = useMemo(
    () =>
      resolvedImplicitAttachments.filter(
        attachment =>
          isAttachmentInScope(attachment, attachmentScopeOwner) &&
          !explicitAttachmentKeys.has(implicitAttachmentKey(attachment)),
      ),
    [attachmentScopeOwner, explicitAttachmentKeys, resolvedImplicitAttachments],
  )
  const implicitChips = useMemo(() => implicitAttachmentChips(implicitAttachments), [implicitAttachments])
  const handleEnvironmentFilesDropped = useCallback(
    (files: FileList) => {
      void environmentAttachments.handleFilesAdded(Array.from(files), 'drop')
    },
    [environmentAttachments],
  )
  const environmentDropTarget = useFileDropTarget(handleEnvironmentFilesDropped)
  const shouldShowModelPicker = isAhpEnvironment || agentSupportsModelSelection(effectiveSelectedAgent, agentSlug)

  const applyNewChatPreset = useCallback(() => {
    setSelectedEnvironmentState(SANDBOX_ENVIRONMENT_OPTION)
    setTargetSelection({kind: 'chat'})
    setSelectedBranch('')
    if (initialText === undefined && !searchParams.get('prompt')) {
      const chatDraftThreadId = getDraftStorageKeySuffix(eventType, 'chat')
      setCurrentPrompt(
        copilotLocalStorage.getSavedUserMessageOnError(chatDraftThreadId) ||
          copilotLocalStorage.getSavedMessage(chatDraftThreadId, 'task') ||
          '',
      )
    }
  }, [
    eventType,
    initialText,
    searchParams,
    setCurrentPrompt,
    setSelectedBranch,
    setSelectedEnvironmentState,
    setTargetSelection,
  ])
  useNewChatPreset({
    presetId: newChatPresetId,
    canApply: canPrepareChat,
    isApplied: isSandboxEnvironment && targetSelection.kind === 'chat',
    apply: applyNewChatPreset,
    textAreaRef,
  })

  const setSelectedEnvironment = useCallback(
    (nextEnvironment: EnvironmentOption) => {
      setSelectedEnvironmentState(nextEnvironment)
      if (nextEnvironment.kind === 'sandbox' && canPrepareChat) {
        handleTargetSelection({kind: 'chat'})
      } else if (targetSelection.kind === 'chat') {
        handleTargetSelection({kind: 'unselected'})
        // Only reachable from Chat, which never holds a repository, so the
        // announcement names what the user now has to do rather than claiming a
        // selection was cleared.
        announce('Chat is only available for Cloud sessions. Choose a repository to create a task.', {
          assertive: false,
          element: textAreaRef.current ?? undefined,
        })
      }
    },
    [canPrepareChat, handleTargetSelection, setSelectedEnvironmentState, targetSelection.kind, textAreaRef],
  )
  const isRepositorySelectionValid =
    (targetSelection.kind === 'repository' && isRepoInScope(targetSelection.repository, scopeOwner)) ||
    (targetSelection.kind === 'chat' &&
      (isSandboxEnvironment || chatLaunch?.kind === 'handoff' || hasImplicitChatAvailability))
  const isSandboxPolicyBlockingSubmission =
    selectedEnvironment?.kind === 'sandbox' && (isSandboxPolicyPending || !supportsSelectedRepositorySandbox)
  const effectiveAgentMode = resolveAvailableAgentMode(agentMode, isAhpEnvironmentDraft)
  const isSelectedEnvironmentSubmissionAvailable =
    targetSelection.kind === 'chat' ? canPrepareChat : Boolean(onEnvironmentSubmit)

  useModelSelectionLoadingAnnouncement(isAhpEnvironment, isModelSelectionReady, modelsError, textAreaRef)
  const chatReference = useMemo(
    () => (referenceRepo && reference ? makeFileOrSnippetReference({reference, referenceRepo}) : undefined),
    [referenceRepo, reference],
  )
  const quotaKind = resolveTaskComposerQuotaKind({targetSelection, chatLaunch, isSandboxDraft})
  const {notice: quotaNotice, isBlocking: isBlockedByQuota} = useEntitlementQuotaNotice(
    quotaKind === 'chat' ? {kind: 'chat', entitlement} : {kind: 'agent', snapshot: agentQuotaSnapshot},
  )

  const {imageReferences, isAtImageLimit: promptImageLimit} = useComposerImageReferences({text: currentPrompt})
  const isAtImageLimit = images.isAtImageLimit ?? promptImageLimit

  const clearPromptDraft = useCallback(
    (submittedContent: string) => {
      // A controlled draft belongs to the parent, which owns both its storage
      // key and its clear. Writing here as well would let this hook clear a key
      // the parent immediately rewrites.
      if (draft) {
        draft.clearDraft(submittedContent)
        return
      }
      if (textAreaRef.current && textAreaRef.current.value !== submittedContent) return
      setCurrentPrompt('')
      copilotLocalStorage.setSavedMessageFast(draftThreadId, 'task', null)
      copilotLocalStorage.clearSavedUserMessageOnError(draftThreadId)
    },
    [draft, draftThreadId, setCurrentPrompt, textAreaRef],
  )

  const {resolve: resolveRepositoryInstructions} = useSelectedRepositoryInstructions()
  const telemetry = useComposerTelemetry()
  const mountedRef = useRef(false)
  const submissionEventRef = useRef<Event | undefined>(undefined)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  const onSubmit = useCallback(
    async (
      content: string,
      initialDirectTurnTelemetrySeed?: DirectTurnTelemetrySeed,
      suppressDirectTurnTelemetryCapture = false,
    ) => {
      const interaction = clientInteractionForEvent(submissionEventRef.current)
      const isRepositoryChatHandoff =
        chatLaunch?.kind === 'handoff' &&
        chatLaunch.submitRepositoryAsChat === true &&
        isSandboxDraft &&
        targetSelection.kind === 'repository'
      const isChatSubmission = targetSelection.kind === 'chat' || isRepositoryChatHandoff
      const selectedChatLaunch = isChatSubmission ? chatLaunch : undefined
      if (
        isBlockedByQuota ||
        !isRepositorySelectionValid ||
        isSandboxPolicyBlockingSubmission ||
        (isChatSubmission && !canPrepareChat && !isRepositoryChatHandoff) ||
        (isAhpEnvironment && (!selectedEnvironment?.environmentId || !isSelectedEnvironmentSubmissionAvailable))
      ) {
        interaction?.failed('handler_rejected')
        telemetry?.submitBlocked('not_ready')
        return
      }
      interaction?.preparationStarted()
      clientInteractionPending(interaction)

      sendEvent('agent_sessions.task_view', {
        target: isChatSubmission ? 'NEW_CHAT_SUBMIT' : 'NEW_TASK_SUBMIT',
      })
      const isDirectTurnTelemetryEligible =
        isSandboxDraft && (selectedChatLaunch?.kind !== 'handoff' || preserveDirectTurnTelemetryForHandoff)
      const directTurnTelemetrySeed: DirectTurnTelemetrySeed | undefined = isDirectTurnTelemetryEligible
        ? (initialDirectTurnTelemetrySeed ??
          (!suppressDirectTurnTelemetryCapture && directTurnTelemetrySurface
            ? {
                requestId: crypto.randomUUID(),
                timing: captureDirectTurnTiming(),
                surface: directTurnTelemetrySurface,
              }
            : undefined))
        : undefined
      telemetry?.noteSubmitDispatch({
        target: isChatSubmission ? 'chat' : 'task',
        directTurnRequestId: directTurnTelemetrySeed?.requestId,
      })

      const attachmentSnapshot = isAhpEnvironmentDraft ? environmentAttachments.snapshot() : undefined
      const submittedInjectedReferences = injectedReferences

      // Save the selected repository to localStorage when task is created
      if (selectedRepo?.id != null) {
        const repo = await fetchRepoSafely(selectedRepo.id)
        if (repo) setLastUsedRepo(repo)
      }

      const modelIdToUse =
        initialPreparedPrompt?.model?.id ??
        selectedModelId ??
        availableModels?.find(m => m.is_chat_default)?.id ??
        availableModels?.[0]?.id
      telemetry?.noteSubmitModel(modelIdToUse)

      // Append file/snippet reference context to the prompt so the agent session
      // has visibility into what the user highlighted when creating the task.
      const contentWithReferences = buildPromptWithReferenceContext({
        content,
        references: [...(chatReference ? [chatReference] : []), ...imageReferences],
        maxPromptLength: MAX_AGENT_TASK_PROMPT_LENGTH,
      })
      const autoTierSupportedForSubmission = copilotFeatureFlags.autoModeV2 && isSandboxDraft && isChatSubmission
      const promptModel = modelIdToUse
        ? sanitizeAutoTierConfig(
            mergeModelSelectionConfig({
              modelId: modelIdToUse,
              baseModel: initialPreparedPrompt?.model,
              updates: {
                reasoningEffort:
                  isReasoningEffortEnabled && selectedReasoningEffortForSession
                    ? selectedReasoningEffortForSession
                    : initialPreparedPrompt?.model?.config?.reasoningEffort,
              },
            }),
            autoTierSupportedForSubmission,
            autoTierSupportedForSubmission ? selectedAutoTier : undefined,
          )
        : undefined
      const environmentRepository =
        isChatSubmission && selectedChatLaunch?.kind === 'environment'
          ? activeInitialChatRepository
          : selectedRepo?.nameWithOwner
      const environmentScopeLogin =
        selectedEnvironment?.kind === 'sandbox'
          ? (environmentRepository?.split('/', 1)[0] ?? launchScopeLogin)
          : undefined
      const provisioningTarget: AhpProvisioningTarget | undefined =
        selectedChatLaunch?.kind === 'environment' && !environmentRepository
          ? initialChatProvisioningTarget?.kind === 'space'
            ? initialChatProvisioningTarget
            : launchScopeLogin
              ? {kind: 'scope', owner: launchScopeLogin}
              : selectedSpace
                ? {kind: 'space', space: selectedSpace}
                : undefined
          : undefined
      const effectiveMcpPermissionMode = resolveMcpPermissionMode({
        approvalMode: isAhpEnvironmentDraft ? approvalMode : undefined,
        agentMode: effectiveAgentMode,
        fallback: mcpPermissionMode,
      })
      // Resolved rather than read off the render, so a submit that beats the
      // page request still carries the page it was composed on.
      interaction?.dependencyChanged('attachments', true)
      const submittedImplicit = await resolveImplicitAttachments(
        collectExplicitAttachmentKeys(
          attachmentSnapshot?.repos ?? [],
          attachmentSnapshot?.files ?? [],
          submittedInjectedReferences,
          attachmentSnapshot?.references ?? [],
        ),
      )
      interaction?.dependencyChanged('attachments', false)
      const intendedAttachments = [
        ...(initialPreparedPrompt?.attachments ?? []),
        ...submittedImplicit,
        ...(attachmentSnapshot?.attachments ?? []),
        ...submittedInjectedReferences.map(item => item.ahp),
      ]
      const attachments = intendedAttachments.filter(attachment =>
        isAttachmentInScope(attachment, attachmentScopeOwner),
      )
      if (activeAttachmentScopeRef.current !== attachmentScopeRevision) {
        throw new ChatInputValidationError('The selected owner changed. Please submit again.')
      }
      const preparedPrompt: PreparedPrompt = recordClientCommandPreparation(
        createPreparedPrompt({
          ...initialPreparedPrompt,
          text: contentWithReferences,
          attachments,
          ...(promptModel ? {model: promptModel} : {}),
          displaySpace: selectedChatLaunch?.kind === 'handoff' || isSandboxEnvironment ? selectedSpace : undefined,
        }),
        attachments.length === intendedAttachments.length,
      )
      prepareClientInteraction(interaction, preparedPrompt)

      if (selectedChatLaunch?.kind === 'handoff') {
        setIsEnvironmentSubmitting(true)
        try {
          const chatMetadata: ChatSubmissionMetadata = {
            ...(selectedSpace ? {space: selectedSpace} : {}),
            ...(activeInitialChatRepository || selectedRepo?.nameWithOwner
              ? {repositoryNameWithOwner: activeInitialChatRepository ?? selectedRepo?.nameWithOwner}
              : {}),
            ...(effectiveAgentMode ? {agentMode: effectiveAgentMode} : {}),
            // Send explicitly (including 'default') so the chat handoff doesn't fall back to stale localStorage.
            mcpPermissionMode: effectiveMcpPermissionMode,
            // The picked owner travels with the turn rather than only labelling
            // the composer, so this launcher scopes the chat the same way the
            // environment launcher does.
            ...(launchScopeLogin ? {scopeLogin: launchScopeLogin} : {}),
            ...(directTurnTelemetrySeed ? {directTurnTelemetrySeed} : {}),
          }
          await selectedChatLaunch.onSubmit(preparedPrompt, chatMetadata)
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to start Chat.'
          throw new ChatInputValidationError(message)
        } finally {
          setIsEnvironmentSubmitting(false)
        }
        attachmentSnapshot?.clear()
        clearInjected(submittedInjectedReferences.map(item => item.token.id))
        settleImplicitAttachments(submittedImplicit)
        clearPromptDraft(content)
        clientInteractionAccepted(interaction)
        return
      }

      // Dispatch to the selected relay-backed environment (sandbox or user-managed
      // AHP), forwarding an explicit repository target + base ref when one exists.
      // A submitter either re-throws for ChatInput recovery or returns prompt
      // ownership when its handoff owns recovery.
      if (isAhpEnvironment) {
        setIsEnvironmentSubmitting(true)
      }
      let repositoryInstructions: CopilotCustomInstructions[] = []
      if (isAhpEnvironment) {
        try {
          repositoryInstructions = await resolveRepositoryInstructions(repositoryInstructionIds(attachments))
        } catch (error) {
          if (isSubmissionAbandoned(error)) {
            interaction?.unresolved('handoff_unobserved')
            return
          }
          throw error
        }
      }
      if (!mountedRef.current || activeAttachmentScopeRef.current !== attachmentScopeRevision) {
        interaction?.unresolved('superseded')
        setIsEnvironmentSubmitting(false)
        return false
      }
      const environmentSubmission = await dispatchEnvironmentSubmissionOrThrow(
        {
          selectedEnvironment,
          onEnvironmentSubmit,
          prompt: preparedPrompt,
          ...(effectiveSelectedAgent.typeName === 'customSubAgent' && effectiveSelectedAgent.subAgentSlug
            ? {
                customAgent: {
                  slug: effectiveSelectedAgent.subAgentSlug,
                  ...(effectiveSelectedAgent.displayName ? {displayName: effectiveSelectedAgent.displayName} : {}),
                },
              }
            : {}),
          ...(repositoryInstructions.length > 0 ? {repositoryInstructions} : {}),
          ...(effectiveAgentMode ? {agentMode: effectiveAgentMode} : {}),
          ...(approvalMode ? {approvalMode} : {}),
          ...(environmentRepository ? {repositoryNwo: environmentRepository} : {}),
          ...(selectedBranch ? {baseRef: selectedBranch} : {}),
          ...(targetSelection.kind === 'chat' &&
          selectedChatLaunch?.kind === 'environment' &&
          selectedEnvironment?.kind === 'sandbox'
            ? {chatLaunch: true}
            : {}),
          ...(isChatSubmission || effectiveMcpPermissionMode !== DEFAULT_MCP_PERMISSION_MODE
            ? {mcpPermissionMode: effectiveMcpPermissionMode}
            : {}),
          ...(selectedChatLaunch?.kind === 'environment' ? {failureOwner: 'caller'} : {}),
          ...(provisioningTarget ? {provisioningTarget} : {}),
          ...(environmentScopeLogin ? {scopeLogin: environmentScopeLogin} : {}),
          ...(isSandboxEnvironment && selectedSpace
            ? {
                space: {owner: selectedSpace.owner, slug: selectedSpace.slug},
                initialSelectedSpace: selectedSpace,
              }
            : {}),
          ...(directTurnTelemetrySeed ? {directTurnTelemetrySeed} : {}),
        },
        () => setIsEnvironmentSubmitting(false),
      )
      if (environmentSubmission.dispatched) {
        if (environmentSubmission.outcome === 'failure-consumed') {
          interaction?.failed('preparation_failed')
          setIsEnvironmentSubmitting(false)
          return false
        }
        attachmentSnapshot?.clear()
        clearInjected(submittedInjectedReferences.map(item => item.token.id))
        settleImplicitAttachments(submittedImplicit)
        if (environmentSubmission.ownsPromptDraft) clearPromptDraft(content)
        clientInteractionAccepted(interaction)
        return
      }
      setIsEnvironmentSubmitting(false)

      await handleUserSubmit(
        contentWithReferences,
        selectedRepo,
        selectedBranch,
        effectiveSelectedAgent,
        modelIdToUse,
        {
          ...(isReasoningEffortEnabled && {reasoningEffort: selectedReasoningEffortForSession}),
          ...(effectiveMcpPermissionMode !== DEFAULT_MCP_PERMISSION_MODE && {
            mcpPermissionMode: effectiveMcpPermissionMode,
          }),
        },
      )
      clearPromptDraft(content)
    },
    [
      selectedRepo,
      attachmentScopeOwner,
      attachmentScopeRevision,
      isRepositorySelectionValid,
      isSandboxPolicyBlockingSubmission,
      isBlockedByQuota,
      canPrepareChat,
      isSelectedEnvironmentSubmissionAvailable,
      targetSelection,
      chatLaunch,
      selectedModelId,
      selectedAutoTier,
      selectedReasoningEffortForSession,
      isReasoningEffortEnabled,
      availableModels,
      selectedEnvironment,
      isAhpEnvironment,
      isSandboxEnvironment,
      isSandboxDraft,
      directTurnTelemetrySurface,
      preserveDirectTurnTelemetryForHandoff,
      isAhpEnvironmentDraft,
      onEnvironmentSubmit,
      selectedBranch,
      effectiveSelectedAgent,
      effectiveAgentMode,
      approvalMode,
      mcpPermissionMode,
      setLastUsedRepo,
      chatReference,
      handleUserSubmit,
      imageReferences,
      clearPromptDraft,
      environmentAttachments,
      injectedReferences,
      clearInjected,
      resolveRepositoryInstructions,
      resolveImplicitAttachments,
      settleImplicitAttachments,
      initialPreparedPrompt,
      initialChatProvisioningTarget,
      launchScopeLogin,
      selectedSpace,
      activeInitialChatRepository,
      setIsEnvironmentSubmitting,
      telemetry,
    ],
  )

  const onSubmitInteraction = useCallback(
    (event: Event, ...args: Parameters<typeof onSubmit>) =>
      submitWithInteractionEvent(submissionEventRef, event, onSubmit, ...args),
    [onSubmit],
  )

  const handleRepoSelect = useCallback(
    (repo: DropdownRepository | undefined) => {
      if (!repo) {
        handleTargetSelection({kind: 'unselected'})
        return
      }

      handleTargetSelection({kind: 'repository', repository: repo})
      setSelectedBranch(repo.defaultBranch || '')
    },
    [handleTargetSelection, setSelectedBranch],
  )

  if (previousRepoParam !== nwoParam) {
    setPreviousRepoParam(nwoParam)
    if (nwoParam && targetSelection.kind !== 'unselected' && selectedRepo?.nameWithOwner !== initialNameWithOwner) {
      setTargetSelection({kind: 'unselected'})
      setSelectedBranch('')
    }
  }

  // Fetch the reference repo. An initial serialized repository is selected
  // asynchronously above, so use the resolved picker repository when it arrives.
  const referenceRepoId = selectedRepo?.id ?? initialRepo?.id
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- async fetch of the reference repo on mount is a genuine effect
    if (referenceRepoId != null) {
      let cancelled = false
      ;(async () => {
        const referenceRepoResult = await fetchRepoSafely(referenceRepoId)
        if (!cancelled && referenceRepoResult) setReferenceRepo(referenceRepoResult)
      })()
      return () => {
        cancelled = true
      }
    }
  }, [referenceRepoId])

  const autoSelectRepo = useEffectEvent((isCancelled: () => boolean) => {
    if (!suppressUrlRepository && initialNameWithOwner) {
      void selectInitialRepo(initialNameWithOwner, isCancelled, applyAutoSelectedRepo)
    }
  })

  useEffect(() => {
    let cancelled = false
    autoSelectRepo(() => cancelled)
    return () => {
      cancelled = true
    }
  }, [initialRepositoryNameWithOwner, nwoParam, suppressUrlRepository])

  // When the scope excludes the selected repo, fall back to Chat if available,
  // otherwise unselect the target.
  useEffect(() => {
    if (isRepoInScope(selectedRepo, scopeOwner)) return

    handleTargetSelection(canPrepareChat ? {kind: 'chat'} : {kind: 'unselected'})
    setSelectedBranch('')
  }, [scopeOwner, selectedRepo, canPrepareChat, handleTargetSelection, setSelectedBranch])

  // When the scope owner changes, drop already-attached repos outside that owner.
  // Keyed on the owner alone (via a ref) so attaching or removing repos never
  // triggers a prune — only an actual owner switch does.
  const previousScopeOwnerRef = useRef(scopeOwner)
  useEffect(() => {
    const previousOwner = previousScopeOwnerRef.current
    previousScopeOwnerRef.current = scopeOwner
    if (previousOwner === scopeOwner) return
    if (scopeOwner === undefined) return

    const keptRepos = environmentAttachments.selectedRepos.filter(repo => isAttachedRepoInScope(repo, scopeOwner))
    if (keptRepos.length !== environmentAttachments.selectedRepos.length) {
      environmentAttachments.handleUpdateRepos(keptRepos)
    }
  }, [scopeOwner, environmentAttachments])

  return {
    currentPrompt,
    setCurrentPrompt,
    handleTextChange,
    handleChronicleSelect,
    setChronicleDismissed,
    chronicleSuggestions,
    draftThreadId,

    selectedRepo,
    provisioningRepository: targetSelection.kind === 'chat' ? activeInitialChatRepository : selectedRepo?.nameWithOwner,
    selectedBranch,
    setSelectedBranch,
    handleRepoSelect,
    selectedAgent: effectiveSelectedAgent,
    setSelectedAgent,
    initialNameWithOwner,
    hasSpecifiedRepository,
    branchParam,
    targetSelection,
    handleTargetSelection,
    selectedSpace,
    setSelectedSpace,
    isSpaceResolving,
    setIsSpaceResolving,
    spaceSelectionError,
    setSpaceSelectionError,

    // Hide the org selection outside Sandbox so consumers never observe a
    // non-personal owner when the picker isn't available.
    selectedScope: exposedSelectedScope,
    setSelectedScope,
    availableOrganizations,
    currentUserAvatarUrl,
    scopeOwner,
    attachmentScopeOwner,
    pickerScopeOwner,

    reference,
    setReference,
    chatReference,
    imageReferences,

    shouldShowModelPicker,
    selectedModelId,
    selectModel,
    isModelSelectionReady,
    vendorFilters,
    isTokenBasedBillingEnabled,
    autoTierPicker,
    selectedReasoningEffort: selectedReasoningEffortForSession,
    setSelectedReasoningEffort,
    supportedReasoningEfforts,

    selectedEnvironment,
    setSelectedEnvironment,
    isAhpEnvironment: isAhpEnvironmentDraft,
    isSandboxEnvironment,
    isSandboxDraft,
    isSandboxPolicyBlockingSubmission,
    isRepositorySelectionValid,
    isEnvironmentSubmitting,
    isScopeBlockedBySso,
    isSelectedEnvironmentSubmissionAvailable,
    canPrepareChat,
    showSandboxOption,
    showEnvironmentPicker,
    mcpPermissionMode,
    setMcpPermissionMode,

    isSubAgentsEnabled,
    isReasoningEffortEnabled,
    isMcpPermissionGateEnabled,

    isCopilotAgent,
    isBlockedByQuota,
    quotaNotice,
    isAtImageLimit,
    resolvedUserLogin: currentUserLogin,

    onPaste: isAhpEnvironment ? undefined : images.onPaste,
    dropTargetProps: isAhpEnvironment ? environmentDropTarget.dropTargetProps : images.dropTargetProps,
    isDraggedOver: isAhpEnvironment ? environmentDropTarget.isDraggedOver : images.isDraggedOver,
    isImageUploading: isAhpEnvironment ? environmentAttachments.isUploading : images.isUploading,

    environmentAttachments,
    chatAttachments,
    implicitChips,
    implicitAttachments,
    dismissImplicitChip,
    injectedReferences,
    removeInjectedReference,

    textAreaRef,
    attachReferenceUrl: draftReferences.attachReferenceUrl,
    onSubmit,
    onSubmitInteraction,

    hideRepoSelector,
    eventType,
    initialBranch,
  }
}
